import * as THREE from 'three'
import type { EventBus } from '@/runtime/EventBus'
import { validateCollisionTransform } from '@/shared/spatial/validateCollisionTransform'
import type { AssetHandle, AssetId, AssetKind, Disposable } from '@/shared/types/core'
import type { SpatialQueryService } from '@/shared/types/services'
import type {
  SharedOcclusionDepthService,
  SharedOcclusionDepthWriter,
} from '@/shared/types/sharedOcclusionDepth'
import type {
  SpatialBindingChangedEventPayload,
  SpatialBindingCreateOptions,
  SpatialBindingFilter,
  SpatialBindingSnapshot,
  SpatialCapsule,
  SpatialCollisionCapsuleQuery,
  SpatialCollisionClosestPointQuery,
  SpatialCollisionGroundHit,
  SpatialCollisionGroundQuery,
  SpatialCollisionHit,
  SpatialCollisionSphereQuery,
  SpatialCollisionSweepCapsuleQuery,
  SpatialNavigationHit,
  SpatialNavigationMotionResult,
  SpatialNavigationProjectPointQuery,
  SpatialNavigationResolveMotionQuery,
  SpatialRepresentationRole,
  SpatialRepresentationSnapshot,
  SpatialSurfaceClosestPointQuery,
  SpatialSurfaceHit,
  SpatialSurfaceRayQuery,
  SpatialSurfaceScreenQuery,
  SpatialSurfaceUsage,
  SpatialWorldHandle,
  SpatialWorldLoadRequest,
  SpatialWorldSnapshot,
} from '@/shared/types/spatial'
import type {
  SpatialCollisionResolveCapsuleQuery,
  SpatialCollisionResolveCapsuleResult,
} from '@/shared/types/spatialCollisionResolve'
import { assertValidPickMinDistance, getPickRayDistance } from '@/shared/utils/pickDepth'
import { type SpatialBindingRecord, SpatialBindingStore } from './SpatialBindingStore'
import { SpatialBuildPipeline } from './SpatialBuildPipeline'
import {
  type SpatialRepresentationRecord,
  type SpatialWorldRecord,
  SpatialWorldStore,
} from './SpatialWorldStore'
import type {
  SpatialCollisionBackend,
  SpatialLocalCollisionGroundHit,
  SpatialLocalCollisionHit,
  SpatialLocalNavigationHit,
  SpatialLocalNavigationMotionResult,
  SpatialLocalSurfaceHit,
  SpatialNavigationBackend,
  SpatialSurfaceBackend,
} from './spatialBackend'
import { ViewRayResolver } from './ViewRayResolver'

/** SpatialQueryService 构造参数 */
export interface SpatialQueryServiceOptions {
  /** 事件总线，用于发射 spatial.* 事件 */
  events: EventBus
  /** Three.js 加载管理器，供 SpatialBuildPipeline 使用 */
  loadingManager: THREE.LoadingManager
  /** 获取容器视口矩形的回调 */
  getViewport: () => { x: number; y: number; width: number; height: number }
  /** 获取 ViewRegistry 的回调；可选 */
  getViewRegistry?: () => import('@/shared/types/services').ViewRegistry | null | undefined
  /** 获取调试 Overlay 根节点的回调；可选 */
  getDebugRoot?: () => THREE.Object3D | null | undefined
  /** 按资源 ID 解析 owner 句柄 */
  resolveOwnerHandle: (kind: AssetKind, id: AssetId) => AssetHandle | undefined
  /** 获取共享遮挡深度服务；可选 */
  getSharedOcclusionDepthService?: () => SharedOcclusionDepthService | null | undefined
  /** 共享遮挡深度 writer 或其 owner/depth proxy 状态变化时触发；可选 */
  onSharedOcclusionDepthChanged?: (reason: string) => void
  /** 是否携带跨域 Cookie */
  withCredentials?: boolean
}

interface SpatialBindingContext {
  binding: SpatialBindingRecord
  snapshot: SpatialBindingSnapshot
  ownerHandle: AssetHandle
  ownerObject: THREE.Object3D
  world: SpatialWorldRecord
  representation: SpatialRepresentationRecord
}

interface SpatialOcclusionDepthWriterRecord {
  bindingId: string
  representationRole: SpatialRepresentationRole
  geometry: THREE.BufferGeometry
  renderGeometry: THREE.BufferGeometry
  ownsRenderGeometry: boolean
  ownerObject: THREE.Object3D
  mesh: THREE.Mesh
  material: THREE.Material
  writer: SharedOcclusionDepthWriter
  registration: Disposable
  signature: string
}

/**
 * SpatialQueryService 默认实现，负责 world 生命周期、绑定管理与各类空间查询。
 */
export class SpatialQueryServiceImpl implements SpatialQueryService {
  private readonly buildPipeline: SpatialBuildPipeline
  private readonly worldStore: SpatialWorldStore
  private readonly bindingStore = new SpatialBindingStore()
  private readonly viewRayResolver: ViewRayResolver
  private readonly debugInstances = new Map<string, THREE.Object3D>()
  private readonly debugInstanceRevisions = new Map<string, number>()
  private readonly occlusionDepthWriters = new Map<string, SpatialOcclusionDepthWriterRecord>()

  private disposed = false

  private readonly handleAssetMounted = (event: { id: AssetId; kind: AssetKind }) => {
    const updated = this.bindingStore.updateOwnerState(event.id, { mounted: true }, event.kind)
    this.emitBindingUpdates('updated', updated)
    this.syncOcclusionDepthWriters()
    if (updated.length > 0) {
      this.options.onSharedOcclusionDepthChanged?.('sharedOcclusionDepth.ownerChanged')
    }
    this.syncDebugOverlays()
  }

  private readonly handleAssetUnmounted = (event: { id: AssetId; kind: AssetKind }) => {
    const updated = this.bindingStore.updateOwnerState(event.id, { mounted: false }, event.kind)
    this.emitBindingUpdates('updated', updated)
    this.syncOcclusionDepthWriters()
    if (updated.length > 0) {
      this.options.onSharedOcclusionDepthChanged?.('sharedOcclusionDepth.ownerChanged')
    }
    this.syncDebugOverlays()
  }

  private readonly handleAssetVisibilityChanged = (event: { id: AssetId; visible: boolean }) => {
    const updated = this.bindingStore.updateOwnerState(event.id, {
      visible: event.visible,
    })
    this.emitBindingUpdates('updated', updated)
    this.syncOcclusionDepthWriters()
    if (updated.length > 0) {
      this.options.onSharedOcclusionDepthChanged?.('sharedOcclusionDepth.ownerChanged')
    }
    this.syncDebugOverlays()
  }

  private readonly handleAssetDisposed = (event: { id: AssetId; kind: AssetKind }) => {
    const removed = this.bindingStore.removeByOwner(event.id, event.kind)
    for (const record of removed) {
      this.worldStore.decrementRef(record.worldId)
      this.emitBindingChanged('unbound', record)
      this.removeDebugInstancesForBinding(record.id)
      this.removeOcclusionDepthWriterForBinding(record.id)
    }
    if (removed.length > 0) {
      this.options.onSharedOcclusionDepthChanged?.('sharedOcclusionDepth.ownerChanged')
    }
  }

  constructor(private readonly options: SpatialQueryServiceOptions) {
    this.buildPipeline = new SpatialBuildPipeline(options.loadingManager, {
      withCredentials: options.withCredentials,
    })
    this.worldStore = new SpatialWorldStore((worldId) => this.unloadWorld(worldId))
    this.viewRayResolver = new ViewRayResolver(options.getViewport, options.getViewRegistry)

    this.options.events.on('asset.mounted', this.handleAssetMounted)
    this.options.events.on('asset.unmounted', this.handleAssetUnmounted)
    this.options.events.on('asset.visibilityChanged', this.handleAssetVisibilityChanged)
    this.options.events.on('asset.disposed', this.handleAssetDisposed)
  }

  /**
   * 加载空间 world，完成后其表达状态变为 ready。
   *
   * @param request - world 加载请求
   * @returns world 句柄，可用于绑定与查询
   */
  loadWorld(request: SpatialWorldLoadRequest): SpatialWorldHandle {
    const requested = getRequestedRepresentations(request)
    if (requested.length === 0) {
      throw new Error('Spatial worlds must request at least one representation')
    }

    const handle = this.worldStore.create({
      id: request.id,
      name: request.name,
      userData: request.userData,
      representations: {
        surface: request.representations.surface
          ? {
              format: request.representations.surface.format ?? null,
              debug: createRequestedDebugSnapshot('surface', request),
              userData: request.representations.surface.userData,
            }
          : undefined,
        collision: request.representations.collision
          ? {
              format: request.representations.collision.format ?? null,
              debug: createRequestedDebugSnapshot('collision', request),
              userData: request.representations.collision.userData,
            }
          : undefined,
        navigation: request.representations.navigation
          ? {
              format: request.representations.navigation.format ?? null,
              debug: createRequestedDebugSnapshot('navigation', request),
              userData: request.representations.navigation.userData,
            }
          : undefined,
      },
    })

    const worldGeneration = this.worldStore.getRecord(handle.id)?.generation ?? 0
    for (const role of requested) {
      const representationRequest = inheritWorldCredentials(
        request.representations[role]!,
        request.withCredentials
      )
      void this.buildRepresentation(
        handle.id,
        worldGeneration,
        role,
        representationRequest,
        request.signal
      )
    }

    return handle
  }

  /**
   * 卸载指定 world，清除其绑定与表达资源。
   *
   * @param id - world ID
   * @returns 成功卸载返回 true；未找到或仍被引用返回 false
   */
  unloadWorld(id: string): boolean {
    const record = this.worldStore.getRecord(id)
    if (!record) {
      return false
    }

    const removedBindings = this.bindingStore.removeByWorldId(id)
    for (const binding of removedBindings) {
      this.emitBindingChanged('unbound', binding)
      this.removeDebugInstancesForBinding(binding.id)
      this.removeOcclusionDepthWriterForBinding(binding.id)
    }

    const removedWorld = this.worldStore.remove(id)
    if (!removedWorld) {
      return false
    }

    for (const representation of Object.values(removedWorld.representations)) {
      representation.backendInstance?.dispose()
      disposeDebugObject(representation.debugPrototype)
    }

    this.removeDebugInstancesForWorld(id)
    this.options.events.emit('spatial.worldDisposed', { worldId: id })
    return true
  }

  /**
   * 获取 world 句柄。
   *
   * @param id - world ID
   * @returns world 句柄；未找到返回 undefined
   */
  getWorld(id: string): SpatialWorldHandle | undefined {
    return this.worldStore.getHandle(id)
  }

  /**
   * 获取 world 快照。
   *
   * @param id - world ID
   * @returns world 快照；未找到返回 undefined
   */
  getWorldSnapshot(id: string): SpatialWorldSnapshot | undefined {
    return this.worldStore.getSnapshot(id)
  }

  /**
   * 列出所有已注册 world 的快照。
   *
   * @returns 快照数组；无 world 返回空数组
   */
  listWorlds(): SpatialWorldSnapshot[] {
    return this.worldStore.listSnapshots()
  }

  /**
   * 将资源绑定到 world。
   *
   * @param options - 绑定创建选项
   * @returns 创建后的绑定快照
   */
  bindWorld(options: SpatialBindingCreateOptions): SpatialBindingSnapshot {
    this.validateCollisionOwner(options)
    const world = this.worldStore.getRecord(options.worldId)
    if (!world) {
      throw new Error(`Spatial world "${options.worldId}" was not found`)
    }

    const ownerState = this.resolveOwnerState(options.ownerKind, options.ownerAssetId)
    const record = this.bindingStore.create(options, ownerState)
    this.worldStore.incrementRef(record.worldId)
    this.emitBindingChanged('bound', record)
    this.syncOcclusionDepthWriters()
    this.syncDebugOverlays()
    return this.bindingStore.snapshot(record, this.getRepresentationState)
  }

  /** 候选 world 不创建绑定，所有输入保持 owner 局部坐标；不进入静态 artifact 缓存。 */
  async prepareCollisionGeometry(
    source: THREE.BufferGeometry,
    signal?: AbortSignal
  ): Promise<SpatialWorldHandle> {
    if (this.disposed) throw new Error('SpatialQueryService 已销毁')
    const handle = this.worldStore.create({
      representations: {
        collision: {
          format: null,
          debug: { prototypeName: null, meshVisible: false, boundsVisible: false, boundsDepth: 0 },
        },
      },
    })
    const generation = this.worldStore.getRecord(handle.id)!.generation
    try {
      const built = await this.buildPipeline.buildRepresentation(
        'collision',
        { source },
        signal,
        false
      )
      if (this.disposed || signal?.aborted || !this.worldStore.getRecord(handle.id)) {
        built.backend.dispose()
        throw new Error('增量碰撞构建已取消')
      }
      this.worldStore.markRepresentationReady(handle.id, 'collision', built, generation)
      return handle
    } catch (error) {
      this.unloadWorld(handle.id)
      throw error
    }
  }

  /** 先校验全部 ready world，原子替换查询集合后再发布事件。 */
  replaceCollisionBindings(
    previousIds: readonly string[],
    next: readonly SpatialBindingCreateOptions[]
  ): readonly SpatialBindingSnapshot[] {
    for (const options of next) {
      this.validateCollisionOwner(options)
      if (this.worldStore.getRepresentationState(options.worldId, 'collision') !== 'ready')
        throw new Error('碰撞候选尚未 ready')
    }
    const added = next.map((options) =>
      this.bindingStore.create(
        { ...options, id: undefined, representationRoles: ['collision'] },
        this.resolveOwnerState(options.ownerKind, options.ownerAssetId)
      )
    )
    for (const record of added) this.worldStore.incrementRef(record.worldId)
    const removed = previousIds.flatMap((id) => {
      const record = this.bindingStore.remove(id)
      if (record) this.worldStore.decrementRef(record.worldId)
      return record ? [record] : []
    })
    for (const record of removed) {
      this.removeDebugInstancesForBinding(record.id)
      this.removeOcclusionDepthWriterForBinding(record.id)
      this.emitBindingChanged('unbound', record)
    }
    for (const record of added) this.emitBindingChanged('bound', record)
    return added.map((record) => this.bindingStore.snapshot(record, this.getRepresentationState))
  }

  private validateCollisionOwner(options: SpatialBindingCreateOptions): void {
    if (options.ownerKind !== 'tiles' || !options.representationRoles.includes('collision')) return
    const owner = this.options.resolveOwnerHandle(options.ownerKind, options.ownerAssetId)?.object3D
    if (!owner) throw new Error('Tiles 碰撞 owner 不存在')
    owner.updateWorldMatrix(true, false)
    validateCollisionTransform(owner.matrixWorld)
  }

  /**
   * 解除指定绑定。
   *
   * @param bindingId - 绑定 ID
   * @returns 成功解除返回 true；未找到返回 false
   */
  unbindWorld(bindingId: string): boolean {
    const record = this.bindingStore.remove(bindingId)
    if (!record) {
      return false
    }

    this.worldStore.decrementRef(record.worldId)
    this.emitBindingChanged('unbound', record)
    this.removeDebugInstancesForBinding(record.id)
    this.removeOcclusionDepthWriterForBinding(record.id)
    return true
  }

  /**
   * 查询匹配条件的绑定快照。
   *
   * @param filter - 过滤条件；省略则返回全部
   * @returns 绑定快照数组
   */
  getBindings(filter?: SpatialBindingFilter): SpatialBindingSnapshot[] {
    return this.bindingStore
      .filter(filter, this.getRepresentationState)
      .map((record) => this.bindingStore.snapshot(record, this.getRepresentationState))
  }

  /**
   * 切换指定角色调试 mesh 的可见性。
   *
   * @param worldId - 目标 world ID
   * @param role - 表达角色
   * @param visible - 是否可见
   * @returns 成功应用返回 true；未找到 world 或角色返回 false
   */
  setDebugMeshVisible(worldId: string, role: SpatialRepresentationRole, visible: boolean): boolean {
    const record = this.worldStore.setRepresentationDebug(worldId, role, {
      meshVisible: visible,
    })
    if (!record) {
      return false
    }

    this.syncDebugOverlays()
    return true
  }

  /**
   * 切换指定角色调试包围盒的可见性。
   *
   * @param worldId - 目标 world ID
   * @param role - 表达角色
   * @param visible - 是否可见
   * @returns 成功应用返回 true；未找到 world 或角色返回 false
   */
  setDebugBoundsVisible(
    worldId: string,
    role: SpatialRepresentationRole,
    visible: boolean
  ): boolean {
    const record = this.worldStore.setRepresentationDebug(worldId, role, {
      boundsVisible: visible,
    })
    if (!record) {
      return false
    }

    this.syncDebugOverlays()
    return true
  }

  /**
   * 设置调试包围盒的可视化层级深度。
   *
   * @param worldId - 目标 world ID
   * @param role - 表达角色
   * @param depth - 深度层数，≥0
   * @returns 成功应用返回 true；未找到 world 或角色返回 false
   */
  setDebugBoundsDepth(worldId: string, role: SpatialRepresentationRole, depth: number): boolean {
    const record = this.worldStore.setRepresentationDebug(worldId, role, {
      boundsDepth: depth,
    })
    if (!record) {
      return false
    }

    this.syncDebugOverlays()
    return true
  }

  /**
   * 射线表面查询。
   *
   * @param query - 射线查询参数
   * @returns 命中信息（世界空间）；未命中返回 null
   */
  raycastSurface(query: SpatialSurfaceRayQuery): SpatialSurfaceHit | null {
    assertValidPickMinDistance(query.minDistance)
    const layerMask = query.layerMask ?? query.camera.layers.mask
    const candidates: SpatialSurfaceHit[] = []

    for (const context of this.collectBindingContexts({
      role: 'surface',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask,
      surfaceUsage: query.surfaceUsage,
    })) {
      const worldRay = query.ray.clone()
      if (query.minDistance !== undefined) {
        worldRay.origin.copy(query.ray.at(query.minDistance, new THREE.Vector3()))
      }
      const localRay = worldRay.applyMatrix4(getInverseOwnerMatrix(context.ownerObject))
      const backend = context.representation.backendInstance as SpatialSurfaceBackend
      const hit = backend.raycastFirst(localRay)
      if (!hit) {
        continue
      }

      const worldPoint = hit.point.clone().applyMatrix4(context.ownerObject.matrixWorld)
      candidates.push(
        this.toSurfaceHit(
          hit,
          context,
          query.surfaceUsage,
          getPickRayDistance(query.ray, worldPoint)
        )
      )
    }

    return pickBestSurfaceHit(candidates, this.bindingStore)
  }

  /**
   * 屏幕坐标派生射线的表面查询。
   *
   * @param query - 屏幕查询参数
   * @returns 命中信息（世界空间）；未命中返回 null
   */
  raycastSurfaceAtScreen(query: SpatialSurfaceScreenQuery): SpatialSurfaceHit | null {
    const resolved = this.viewRayResolver.resolve(query)
    if (!resolved) {
      return null
    }

    return this.raycastSurface({
      ...query,
      camera: resolved.camera,
      ray: resolved.ray,
      layerMask: resolved.layerMask ?? query.layerMask,
    })
  }

  /**
   * 表面最近点查询。
   *
   * @param query - 最近点查询参数
   * @returns 命中信息；超距离或未命中返回 null
   */
  closestSurfacePoint(query: SpatialSurfaceClosestPointQuery): SpatialSurfaceHit | null {
    const candidates: SpatialSurfaceHit[] = []
    for (const context of this.collectBindingContexts({
      role: 'surface',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask: query.layerMask,
      surfaceUsage: query.surfaceUsage,
    })) {
      const localPoint = query.point
        .clone()
        .applyMatrix4(getInverseOwnerMatrix(context.ownerObject))
      const backend = context.representation.backendInstance as SpatialSurfaceBackend
      const hit = backend.closestPoint(localPoint, query.maxDistance)
      if (!hit) {
        continue
      }

      const worldPoint = hit.point.clone().applyMatrix4(context.ownerObject.matrixWorld)
      candidates.push(
        this.toSurfaceHit(hit, context, query.surfaceUsage, query.point.distanceTo(worldPoint))
      )
    }

    return pickBestSurfaceHit(candidates, this.bindingStore)
  }

  /**
   * 球体碰撞重叠查询。
   *
   * @param query - 球体查询参数
   * @returns 碰撞信息含穿透深度；未穿透返回 null
   */
  overlapCollisionSphere(query: SpatialCollisionSphereQuery): SpatialCollisionHit | null {
    return this.routeCollisionSphere(query)
  }

  /**
   * 胶囊体重叠碰撞查询。
   *
   * @param query - 重叠查询参数
   * @returns 碰撞信息含穿透深度；未穿透返回 null
   */
  overlapCollisionCapsule(query: SpatialCollisionCapsuleQuery): SpatialCollisionHit | null {
    return this.routeCollisionCapsuleOverlap(query)
  }

  /**
   * 胶囊体穿透解算查询。
   *
   * @param query - 穿透解算查询参数
   * @returns 解算结果；未穿透返回 null
   */
  resolveCollisionCapsule(
    query: SpatialCollisionResolveCapsuleQuery
  ): SpatialCollisionResolveCapsuleResult | null {
    return this.routeCollisionCapsuleResolve(query)
  }

  /**
   * 胶囊体扫掠碰撞查询。
   *
   * @param query - 扫掠查询参数
   * @returns 最先碰撞信息；未碰撞返回 null
   */
  sweepCollisionCapsule(query: SpatialCollisionSweepCapsuleQuery): SpatialCollisionHit | null {
    return this.routeCollisionCapsuleSweep(query)
  }

  /**
   * 碰撞最近点查询。
   *
   * @param query - 最近点查询参数
   * @returns 碰撞信息；未命中返回 null
   */
  closestCollisionPoint(query: SpatialCollisionClosestPointQuery): SpatialCollisionHit | null {
    return this.routeCollisionClosestPoint(query)
  }

  /**
   * 地面碰撞查询。
   *
   * @param query - 地面查询参数
   * @returns 地面命中信息；未命中返回 null
   */
  resolveCollisionGround(query: SpatialCollisionGroundQuery): SpatialCollisionGroundHit | null {
    return this.routeCollisionGround(query)
  }

  /**
   * 导航面投影查询。
   *
   * @param query - 导航点查询参数
   * @returns 导航命中信息；未命中返回 null
   */
  projectNavigationPoint(query: SpatialNavigationProjectPointQuery): SpatialNavigationHit | null {
    return this.routeNavigationPoint(query)
  }

  /**
   * 导航运动解算。
   *
   * @param query - 运动查询参数
   * @returns 导航运动结果；全程受阻返回 null
   */
  resolveNavigationMotion(
    query: SpatialNavigationResolveMotionQuery
  ): SpatialNavigationMotionResult | null {
    return this.routeNavigationMotion(query)
  }

  /** 每帧更新调试 Overlay 同步 */
  update(): void {
    this.syncOcclusionDepthWriters()
    this.syncDebugOverlays()
  }

  /** 释放服务，卸载所有 world 并取消事件订阅 */
  dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true

    this.options.events.off('asset.mounted', this.handleAssetMounted)
    this.options.events.off('asset.unmounted', this.handleAssetUnmounted)
    this.options.events.off('asset.visibilityChanged', this.handleAssetVisibilityChanged)
    this.options.events.off('asset.disposed', this.handleAssetDisposed)

    this.clearOcclusionDepthWriters()
    this.clearDebugInstances()
    for (const world of this.worldStore.clear()) {
      for (const representation of Object.values(world.representations)) {
        representation.backendInstance?.dispose()
        disposeDebugObject(representation.debugPrototype)
      }
    }
    this.buildPipeline.dispose()
  }

  private routeCollisionSphere(query: SpatialCollisionSphereQuery): SpatialCollisionHit | null {
    const candidates: SpatialCollisionHit[] = []
    for (const context of this.collectBindingContexts({
      role: 'collision',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask: query.layerMask,
    })) {
      const scale = getOwnerScale(context.ownerObject)
      const localSphere = new THREE.Sphere(
        query.sphere.center.clone().applyMatrix4(getInverseOwnerMatrix(context.ownerObject)),
        query.sphere.radius / scale
      )
      const backend = context.representation.backendInstance as SpatialCollisionBackend
      const hit = backend.overlapSphere(localSphere)
      if (!hit) {
        continue
      }

      candidates.push(
        this.toCollisionHit(
          hit,
          context,
          query.sphere.center.distanceTo(
            hit.point.clone().applyMatrix4(context.ownerObject.matrixWorld)
          ),
          scale
        )
      )
    }

    return pickBestCollisionHit(candidates, this.bindingStore)
  }

  private routeCollisionCapsuleOverlap(
    query: SpatialCollisionCapsuleQuery
  ): SpatialCollisionHit | null {
    const candidates: SpatialCollisionHit[] = []
    for (const context of this.collectBindingContexts({
      role: 'collision',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask: query.layerMask,
    })) {
      const scale = getOwnerScale(context.ownerObject)
      const localCapsule = createLocalCapsule(context.ownerObject, query.capsule, scale)
      const backend = context.representation.backendInstance as SpatialCollisionBackend
      const hit = backend.overlapCapsule(localCapsule)
      if (!hit) {
        continue
      }

      candidates.push(
        this.toCollisionHit(
          hit,
          context,
          query.capsule.start.distanceTo(
            hit.point.clone().applyMatrix4(context.ownerObject.matrixWorld)
          ),
          scale
        )
      )
    }

    return pickBestCollisionHit(candidates, this.bindingStore)
  }

  private routeCollisionCapsuleResolve(
    query: SpatialCollisionResolveCapsuleQuery
  ): SpatialCollisionResolveCapsuleResult | null {
    const workingCapsule = cloneWorldCapsule(query.capsule)
    const appliedMotion = new THREE.Vector3()
    const maxIterations = Math.max(1, query.maxIterations ?? 8)
    const skinWidth = Math.max(0, query.skinWidth ?? 0)
    let lastHit: SpatialCollisionHit | null = null
    let iterationCount = 0

    for (; iterationCount < maxIterations; iterationCount += 1) {
      const hit = this.routeCollisionCapsuleOverlap({
        ...query,
        capsule: workingCapsule,
      })
      if (!hit) {
        break
      }

      const normal = normalizeWorldHitNormal(hit)
      const penetrationDepth = Math.max(hit.penetrationDepth ?? 0, 0)
      if (!normal || penetrationDepth <= 1e-8) {
        lastHit = hit
        break
      }

      const correction = normal.multiplyScalar(penetrationDepth + skinWidth)
      translateWorldCapsule(workingCapsule, correction)
      appliedMotion.add(correction)
      lastHit = hit
    }

    if (!lastHit || appliedMotion.lengthSq() <= 1e-12) {
      return null
    }

    const separationNormal = appliedMotion.clone().normalize()
    return {
      ...lastHit,
      distance: appliedMotion.length(),
      penetrationDepth: appliedMotion.length(),
      separationNormal,
      resolvedCapsule: cloneWorldCapsule(workingCapsule),
      appliedMotion,
      iterationCount,
    }
  }

  private routeCollisionCapsuleSweep(
    query: SpatialCollisionSweepCapsuleQuery
  ): SpatialCollisionHit | null {
    const candidates: SpatialCollisionHit[] = []
    for (const context of this.collectBindingContexts({
      role: 'collision',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask: query.layerMask,
    })) {
      const inverse = getInverseOwnerMatrix(context.ownerObject)
      const scale = getOwnerScale(context.ownerObject)
      const localCapsule = createLocalCapsule(context.ownerObject, query.capsule, scale)
      const localStart = localCapsule.start
      const localMotion = query.capsule.start
        .clone()
        .add(query.motion)
        .applyMatrix4(inverse)
        .sub(localStart)

      const backend = context.representation.backendInstance as SpatialCollisionBackend
      const hit = backend.sweepCapsule(localCapsule, localMotion, query.maxSteps)
      if (!hit) {
        continue
      }

      candidates.push(this.toCollisionHit(hit, context, hit.distance * scale, scale))
    }

    return pickBestCollisionHit(candidates, this.bindingStore)
  }

  private routeCollisionClosestPoint(
    query: SpatialCollisionClosestPointQuery
  ): SpatialCollisionHit | null {
    const candidates: SpatialCollisionHit[] = []
    for (const context of this.collectBindingContexts({
      role: 'collision',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask: query.layerMask,
    })) {
      const inverse = getInverseOwnerMatrix(context.ownerObject)
      const scale = getOwnerScale(context.ownerObject)
      const localPoint = query.point.clone().applyMatrix4(inverse)
      const backend = context.representation.backendInstance as SpatialCollisionBackend
      const hit = backend.closestPoint(
        localPoint,
        query.maxDistance !== undefined ? query.maxDistance / scale : undefined
      )
      if (!hit) {
        continue
      }

      const worldPoint = hit.point.clone().applyMatrix4(context.ownerObject.matrixWorld)
      candidates.push(this.toCollisionHit(hit, context, query.point.distanceTo(worldPoint), scale))
    }

    return pickBestCollisionHit(candidates, this.bindingStore)
  }

  private routeCollisionGround(
    query: SpatialCollisionGroundQuery
  ): SpatialCollisionGroundHit | null {
    const candidates: SpatialCollisionGroundHit[] = []
    for (const context of this.collectBindingContexts({
      role: 'collision',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask: query.layerMask,
    })) {
      const inverse = getInverseOwnerMatrix(context.ownerObject)
      const scale = getOwnerScale(context.ownerObject)
      const localOrigin = query.origin.clone().applyMatrix4(inverse)
      const localDown = query.down
        ? query.origin.clone().add(query.down).applyMatrix4(inverse).sub(localOrigin)
        : undefined
      const backend = context.representation.backendInstance as SpatialCollisionBackend
      const hit = backend.resolveGround(
        localOrigin,
        localDown,
        query.maxDistance !== undefined ? query.maxDistance / scale : undefined,
        query.maxSlopeAngle
      )
      if (!hit) {
        continue
      }

      candidates.push(
        this.toCollisionGroundHit(
          hit,
          context,
          query.origin.distanceTo(hit.point.clone().applyMatrix4(context.ownerObject.matrixWorld)),
          scale
        )
      )
    }

    return pickBestCollisionGroundHit(candidates, this.bindingStore)
  }

  private routeNavigationPoint(
    query: SpatialNavigationProjectPointQuery
  ): SpatialNavigationHit | null {
    const candidates: SpatialNavigationHit[] = []
    for (const context of this.collectBindingContexts({
      role: 'navigation',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask: query.layerMask,
    })) {
      const inverse = getInverseOwnerMatrix(context.ownerObject)
      const scale = getOwnerScale(context.ownerObject)
      const localPoint = query.point.clone().applyMatrix4(inverse)
      const backend = context.representation.backendInstance as SpatialNavigationBackend
      const hit = backend.projectPoint(
        localPoint,
        query.maxDistance !== undefined ? query.maxDistance / scale : undefined,
        query.requiredTags,
        query.blockedTags
      )
      if (!hit) {
        continue
      }

      const worldPoint = hit.point.clone().applyMatrix4(context.ownerObject.matrixWorld)
      candidates.push(this.toNavigationHit(hit, context, query.point.distanceTo(worldPoint)))
    }

    return pickBestNavigationHit(candidates, this.bindingStore)
  }

  private routeNavigationMotion(
    query: SpatialNavigationResolveMotionQuery
  ): SpatialNavigationMotionResult | null {
    const candidates: SpatialNavigationMotionResult[] = []
    for (const context of this.collectBindingContexts({
      role: 'navigation',
      ownerAssetId: query.ownerAssetId,
      ownerKind: query.ownerKind,
      worldId: query.worldId,
      layerMask: query.layerMask,
    })) {
      const inverse = getInverseOwnerMatrix(context.ownerObject)
      const scale = getOwnerScale(context.ownerObject)
      const localStart = query.start.clone().applyMatrix4(inverse)
      const localMotion = query.start
        .clone()
        .add(query.motion)
        .applyMatrix4(inverse)
        .sub(localStart)
      const backend = context.representation.backendInstance as SpatialNavigationBackend
      const hit = backend.resolveMotion(
        localStart,
        localMotion,
        query.maxDistance !== undefined ? query.maxDistance / scale : undefined,
        query.requiredTags,
        query.blockedTags,
        query.samples
      )
      if (!hit) {
        continue
      }

      candidates.push(this.toNavigationMotionResult(hit, context))
    }

    return pickBestNavigationMotionResult(candidates, this.bindingStore)
  }

  private async buildRepresentation(
    worldId: string,
    worldGeneration: number,
    role: SpatialRepresentationRole,
    request: NonNullable<SpatialWorldLoadRequest['representations'][SpatialRepresentationRole]>,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      const built = await this.buildPipeline.buildRepresentation(role, request, signal)
      const representation = this.worldStore.markRepresentationReady(
        worldId,
        role,
        built,
        worldGeneration
      )
      if (!representation) {
        built.backend.dispose()
        return
      }

      this.emitRepresentationReady(worldId, role)
      this.emitBindingUpdatesForWorld(worldId)
      this.syncOcclusionDepthWriters()
      this.syncDebugOverlays()
    } catch (error) {
      const normalized = normalizeError(error)
      const representation = this.worldStore.markRepresentationError(
        worldId,
        role,
        normalized,
        worldGeneration
      )
      if (!representation) {
        return
      }

      this.emitRepresentationError(worldId, role, normalized)
      this.emitBindingUpdatesForWorld(worldId)
      this.syncOcclusionDepthWriters()
      this.syncDebugOverlays()
    }
  }

  private collectBindingContexts(options: {
    role: SpatialRepresentationRole
    ownerAssetId?: AssetId
    ownerKind?: AssetKind
    worldId?: string
    layerMask?: number
    surfaceUsage?: SpatialSurfaceUsage
  }): SpatialBindingContext[] {
    const records = this.bindingStore.filter(
      {
        ownerAssetId: options.ownerAssetId,
        ownerKind: options.ownerKind,
        worldId: options.worldId,
        representationRole: options.role,
        surfaceUsage: options.surfaceUsage,
      },
      this.getRepresentationState
    )

    const contexts: SpatialBindingContext[] = []
    for (const binding of records) {
      const world = this.worldStore.getRecord(binding.worldId)
      const representation = world?.representations[options.role]
      if (
        !world ||
        !representation ||
        representation.state !== 'ready' ||
        !representation.backendInstance
      ) {
        continue
      }

      const snapshot = this.bindingStore.snapshot(binding, this.getRepresentationState)
      if (!snapshot.availabilityByRole[options.role]) {
        continue
      }

      const ownerHandle = this.options.resolveOwnerHandle(binding.ownerKind, binding.ownerAssetId)
      const ownerObject = ownerHandle?.object3D ?? null
      if (!ownerHandle || !ownerObject) {
        continue
      }

      if (options.layerMask !== undefined && (ownerObject.layers.mask & options.layerMask) === 0) {
        continue
      }

      ownerObject.updateMatrixWorld(true)
      if (binding.ownerKind === 'tiles' && options.role === 'collision') {
        try {
          validateCollisionTransform(ownerObject.matrixWorld)
        } catch {
          continue
        }
      }
      contexts.push({
        binding,
        snapshot,
        ownerHandle,
        ownerObject,
        world,
        representation,
      })
    }

    return contexts
  }

  private toSurfaceHit(
    hit: SpatialLocalSurfaceHit,
    context: SpatialBindingContext,
    surfaceUsage: SpatialSurfaceUsage,
    distance: number
  ): SpatialSurfaceHit {
    const worldMatrix = context.ownerObject.matrixWorld
    return {
      worldId: context.binding.worldId,
      bindingId: context.binding.id,
      ownerAssetId: context.binding.ownerAssetId,
      ownerKind: context.binding.ownerKind,
      representationRole: 'surface',
      surfaceUsage,
      point: hit.point.clone().applyMatrix4(worldMatrix),
      distance,
      normal: hit.normal?.clone().transformDirection(worldMatrix),
      faceIndex: hit.faceIndex,
      metadata: hit.metadata,
      raw: hit.raw,
    }
  }

  private toCollisionHit(
    hit: SpatialLocalCollisionHit,
    context: SpatialBindingContext,
    distance: number,
    scale: number
  ): SpatialCollisionHit {
    const worldMatrix = context.ownerObject.matrixWorld
    return {
      worldId: context.binding.worldId,
      bindingId: context.binding.id,
      ownerAssetId: context.binding.ownerAssetId,
      ownerKind: context.binding.ownerKind,
      representationRole: 'collision',
      point: hit.point.clone().applyMatrix4(worldMatrix),
      distance,
      normal: hit.normal?.clone().transformDirection(worldMatrix),
      faceIndex: hit.faceIndex,
      metadata: hit.metadata,
      penetrationDepth:
        hit.penetrationDepth !== undefined ? hit.penetrationDepth * scale : undefined,
      travelDistance: hit.travelDistance !== undefined ? hit.travelDistance * scale : undefined,
      separationNormal: hit.separationNormal?.clone().transformDirection(worldMatrix),
      raw: hit.raw,
    }
  }

  private toCollisionGroundHit(
    hit: SpatialLocalCollisionGroundHit,
    context: SpatialBindingContext,
    distance: number,
    scale: number
  ): SpatialCollisionGroundHit {
    return {
      ...this.toCollisionHit(hit, context, distance, scale),
      slopeAngle: hit.slopeAngle,
    }
  }

  private toNavigationHit(
    hit: SpatialLocalNavigationHit,
    context: SpatialBindingContext,
    distance: number
  ): SpatialNavigationHit {
    const worldMatrix = context.ownerObject.matrixWorld
    return {
      worldId: context.binding.worldId,
      bindingId: context.binding.id,
      ownerAssetId: context.binding.ownerAssetId,
      ownerKind: context.binding.ownerKind,
      representationRole: 'navigation',
      point: hit.point.clone().applyMatrix4(worldMatrix),
      distance,
      normal: hit.normal?.clone().transformDirection(worldMatrix),
      faceIndex: hit.faceIndex,
      metadata: hit.metadata,
      semanticTags: hit.semanticTags,
      walkable: hit.walkable,
      raw: hit.raw,
    }
  }

  private toNavigationMotionResult(
    hit: SpatialLocalNavigationMotionResult,
    context: SpatialBindingContext
  ): SpatialNavigationMotionResult {
    const worldMatrix = context.ownerObject.matrixWorld
    const resolvedPoint = hit.resolvedPoint.clone().applyMatrix4(worldMatrix)
    const startPoint = hit.resolvedPoint.clone().sub(hit.appliedMotion).applyMatrix4(worldMatrix)
    return {
      ...this.toNavigationHit(hit, context, hit.appliedMotion.length()),
      resolvedPoint,
      appliedMotion: resolvedPoint.clone().sub(startPoint),
      blocked: hit.blocked,
    }
  }

  private resolveOwnerState(
    ownerKind: AssetKind,
    ownerAssetId: AssetId
  ): { mounted: boolean; visible: boolean } {
    const ownerHandle = this.options.resolveOwnerHandle(ownerKind, ownerAssetId)
    const ownerObject = ownerHandle?.object3D ?? null
    return {
      mounted: !!ownerObject?.parent,
      visible: ownerHandle?.visible ?? false,
    }
  }

  private getRepresentationState = (worldId: string, role: SpatialRepresentationRole) =>
    this.worldStore.getRepresentationState(worldId, role)

  private emitRepresentationReady(worldId: string, role: SpatialRepresentationRole): void {
    const representation = this.worldStore.getRepresentationRecord(worldId, role)
    if (!representation) {
      return
    }

    this.options.events.emit('spatial.representationReady', {
      worldId,
      representationRole: role,
      representation: cloneRepresentationSnapshot(representation),
    })
  }

  private emitRepresentationError(
    worldId: string,
    role: SpatialRepresentationRole,
    error: Error
  ): void {
    const representation = this.worldStore.getRepresentationRecord(worldId, role)
    if (!representation) {
      return
    }

    this.options.events.emit('spatial.representationError', {
      worldId,
      representationRole: role,
      representation: cloneRepresentationSnapshot(representation),
      error,
    })
  }

  private emitBindingUpdatesForWorld(worldId: string): void {
    const bindings = this.bindingStore.filter(
      {
        worldId,
        includeInactive: true,
      },
      this.getRepresentationState
    )
    this.emitBindingUpdates('updated', bindings)
  }

  private emitBindingUpdates(
    action: SpatialBindingChangedEventPayload['action'],
    bindings: SpatialBindingRecord[]
  ): void {
    for (const binding of bindings) {
      this.emitBindingChanged(action, binding)
    }
  }

  private emitBindingChanged(
    action: SpatialBindingChangedEventPayload['action'],
    record: SpatialBindingRecord
  ): void {
    const snapshot = this.bindingStore.snapshot(record, this.getRepresentationState)
    this.options.events.emit('spatial.bindingChanged', {
      action,
      binding: snapshot,
      ownerAssetId: record.ownerAssetId,
      ownerKind: record.ownerKind,
      worldId: record.worldId,
    })
  }

  private syncOcclusionDepthWriters(): void {
    const service = this.options.getSharedOcclusionDepthService?.() ?? null
    if (!service) {
      this.clearOcclusionDepthWriters()
      return
    }

    const activeKeys = new Set<string>()
    for (const binding of this.bindingStore.filter(
      { includeInactive: true },
      this.getRepresentationState
    )) {
      const role = binding.occlusionDepth.representationRole
      if (!binding.occlusionDepth.enabled || !role) {
        this.removeOcclusionDepthWriterForBinding(binding.id)
        continue
      }

      const snapshot = this.bindingStore.snapshot(binding, this.getRepresentationState)
      const representation = this.worldStore.getRepresentationRecord(binding.worldId, role)
      const geometry = representation?.backendInstance?.getDepthGeometry?.() ?? null
      const ownerHandle = this.options.resolveOwnerHandle(binding.ownerKind, binding.ownerAssetId)
      const ownerObject = ownerHandle?.object3D ?? null

      if (
        !representation ||
        representation.state !== 'ready' ||
        !snapshot.availabilityByRole[role] ||
        !geometry ||
        !ownerHandle ||
        !ownerObject
      ) {
        this.removeOcclusionDepthWriterForBinding(binding.id)
        continue
      }

      const key = createOcclusionDepthWriterKey(binding.id)
      activeKeys.add(key)
      const existing = this.occlusionDepthWriters.get(key)
      if (
        existing &&
        (existing.geometry !== geometry ||
          existing.representationRole !== role ||
          existing.ownerObject !== ownerObject)
      ) {
        this.removeOcclusionDepthWriterForBinding(binding.id)
      }

      if (!this.occlusionDepthWriters.has(key)) {
        this.occlusionDepthWriters.set(
          key,
          createSpatialOcclusionDepthWriterRecord({
            binding,
            geometry,
            ownerObject,
            service,
          })
        )
        this.options.onSharedOcclusionDepthChanged?.('sharedOcclusionDepth.writerChanged')
        continue
      }

      const record = this.occlusionDepthWriters.get(key)
      if (!record) {
        continue
      }
      const nextSignature = createOcclusionDepthWriterSignature(ownerObject, geometry)
      if (record.signature !== nextSignature) {
        record.signature = nextSignature
        this.options.onSharedOcclusionDepthChanged?.('sharedOcclusionDepth.ownerChanged')
      }
    }

    for (const key of Array.from(this.occlusionDepthWriters.keys())) {
      if (!activeKeys.has(key)) {
        this.removeOcclusionDepthWriterByKey(key)
      }
    }
  }

  private clearOcclusionDepthWriters(): void {
    for (const key of Array.from(this.occlusionDepthWriters.keys())) {
      this.removeOcclusionDepthWriterByKey(key)
    }
  }

  private removeOcclusionDepthWriterForBinding(bindingId: string): void {
    this.removeOcclusionDepthWriterByKey(createOcclusionDepthWriterKey(bindingId))
  }

  private removeOcclusionDepthWriterByKey(key: string): void {
    const record = this.occlusionDepthWriters.get(key)
    if (!record) {
      return
    }

    this.occlusionDepthWriters.delete(key)
    record.registration.dispose()
    record.mesh.removeFromParent()
    if (record.ownsRenderGeometry) {
      record.renderGeometry.dispose()
    }
    record.material.dispose()
    this.options.onSharedOcclusionDepthChanged?.('sharedOcclusionDepth.writerChanged')
  }

  private syncDebugOverlays(): void {
    const debugRoot = this.options.getDebugRoot?.() ?? null
    if (!debugRoot) {
      this.clearDebugInstances()
      return
    }

    const activeKeys = new Set<string>()
    for (const binding of this.bindingStore.filter(
      { includeInactive: true },
      this.getRepresentationState
    )) {
      const snapshot = this.bindingStore.snapshot(binding, this.getRepresentationState)
      const ownerHandle = this.options.resolveOwnerHandle(binding.ownerKind, binding.ownerAssetId)
      const ownerObject = ownerHandle?.object3D ?? null
      if (!ownerObject) {
        this.removeDebugInstancesForBinding(binding.id)
        continue
      }

      ownerObject.updateMatrixWorld(true)
      for (const role of binding.representationRoles) {
        const representation = this.worldStore.getRepresentationRecord(binding.worldId, role)
        const debug = representation?.debug
        if (
          !representation ||
          representation.state !== 'ready' ||
          !representation.backendInstance ||
          !debug ||
          !representation.debugPrototype ||
          !snapshot.availabilityByRole[role] ||
          (!debug.meshVisible && !debug.boundsVisible)
        ) {
          this.removeDebugInstance(binding.id, role)
          continue
        }

        const key = createDebugInstanceKey(binding.id, role)
        activeKeys.add(key)
        let instance = this.debugInstances.get(key)
        if (instance && this.debugInstanceRevisions.get(key) !== representation.debugRevision) {
          this.removeDebugInstance(binding.id, role)
          instance = undefined
        }

        if (!instance) {
          const created = representation.backendInstance.createDebugPrototype(
            `${binding.id}-${role}-debug`,
            debug
          )
          if (!created) {
            continue
          }
          instance = created
          this.debugInstances.set(key, instance)
          this.debugInstanceRevisions.set(key, representation.debugRevision)
          debugRoot.add(instance)
        }

        if (instance.parent !== debugRoot) {
          instance.removeFromParent()
          debugRoot.add(instance)
        }

        syncDebugInstanceTransform(instance, ownerObject, debugRoot)
      }
    }

    for (const key of Array.from(this.debugInstances.keys())) {
      if (!activeKeys.has(key)) {
        const instance = this.debugInstances.get(key)
        if (!instance) {
          continue
        }
        this.debugInstances.delete(key)
        disposeDebugObject(instance)
      }
    }
  }

  private clearDebugInstances(): void {
    for (const instance of this.debugInstances.values()) {
      disposeDebugObject(instance)
    }
    this.debugInstances.clear()
    this.debugInstanceRevisions.clear()
  }

  private removeDebugInstancesForWorld(worldId: string): void {
    for (const binding of this.bindingStore.filter(
      {
        worldId,
        includeInactive: true,
      },
      this.getRepresentationState
    )) {
      this.removeDebugInstancesForBinding(binding.id)
    }
  }

  private removeDebugInstancesForBinding(bindingId: string): void {
    this.removeDebugInstance(bindingId, 'surface')
    this.removeDebugInstance(bindingId, 'collision')
    this.removeDebugInstance(bindingId, 'navigation')
  }

  private removeDebugInstance(bindingId: string, role: SpatialRepresentationRole): void {
    const key = createDebugInstanceKey(bindingId, role)
    const instance = this.debugInstances.get(key)
    if (!instance) {
      return
    }

    this.debugInstances.delete(key)
    this.debugInstanceRevisions.delete(key)
    disposeDebugObject(instance)
  }
}

function getRequestedRepresentations(
  request: SpatialWorldLoadRequest
): SpatialRepresentationRole[] {
  return (['surface', 'collision', 'navigation'] as SpatialRepresentationRole[]).filter(
    (role) => !!request.representations[role]
  )
}

type SpatialRepresentationRequest = NonNullable<
  SpatialWorldLoadRequest['representations'][SpatialRepresentationRole]
>

function inheritWorldCredentials(
  request: SpatialRepresentationRequest,
  withCredentials: boolean | undefined
): SpatialRepresentationRequest {
  if (request.withCredentials !== undefined || withCredentials === undefined) {
    return request
  }

  return {
    ...request,
    withCredentials,
  }
}

function createSpatialOcclusionDepthWriterRecord(options: {
  binding: SpatialBindingRecord
  geometry: THREE.BufferGeometry
  ownerObject: THREE.Object3D
  service: SharedOcclusionDepthService
}): SpatialOcclusionDepthWriterRecord {
  const material = new THREE.MeshBasicMaterial({
    colorWrite: false,
    depthTest: true,
    depthWrite: true,
    side: THREE.DoubleSide,
  })
  const renderGeometry = createDepthOnlyGeometry(options.geometry)
  const mesh = new THREE.Mesh(renderGeometry, material)
  mesh.name = `${options.binding.id}-occlusion-depth`
  mesh.frustumCulled = false
  mesh.matrixAutoUpdate = false

  const writer: SharedOcclusionDepthWriter = {
    id: mesh.name,
    priority: -options.binding.priority,
    collect: () => {
      options.ownerObject.updateMatrixWorld(true)
      mesh.matrix.copy(options.ownerObject.matrixWorld)
      mesh.matrixWorld.copy(options.ownerObject.matrixWorld)
      mesh.layers.mask = options.ownerObject.layers.mask
      mesh.visible = options.ownerObject.visible
      return mesh.visible ? [{ object: mesh }] : []
    },
  }

  return {
    bindingId: options.binding.id,
    representationRole: options.binding.occlusionDepth.representationRole ?? 'surface',
    geometry: options.geometry,
    renderGeometry,
    ownsRenderGeometry: renderGeometry !== options.geometry,
    ownerObject: options.ownerObject,
    mesh,
    material,
    writer,
    registration: options.service.registerWriter(writer),
    signature: createOcclusionDepthWriterSignature(options.ownerObject, options.geometry),
  }
}

/** 构造仅供深度写入使用的几何：展开 interleaved 属性，保留索引与 groups。 */
function createDepthOnlyGeometry(source: THREE.BufferGeometry): THREE.BufferGeometry {
  const position = source.getAttribute('position')
  if (!position) {
    return source
  }

  const positionArray = new Float32Array(position.count * position.itemSize)
  copyAttributeComponents(position, positionArray)

  const geometry = new THREE.BufferGeometry()
  geometry.name = source.name ? `${source.name}-depth` : `${source.uuid}-depth`
  geometry.userData = { ...source.userData }
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(positionArray, position.itemSize, position.normalized)
  )

  const index = source.getIndex()
  if (index) {
    const indexArray = new Uint32Array(index.count)
    for (let item = 0; item < index.count; item += 1) {
      indexArray[item] = index.getX(item)
    }
    geometry.setIndex(new THREE.BufferAttribute(indexArray, 1))
  }

  for (const group of source.groups) {
    geometry.addGroup(group.start, group.count, group.materialIndex)
  }

  if (source.boundingBox) {
    geometry.boundingBox = source.boundingBox.clone()
  } else {
    geometry.computeBoundingBox()
  }

  if (source.boundingSphere) {
    geometry.boundingSphere = source.boundingSphere.clone()
  } else {
    geometry.computeBoundingSphere()
  }

  return geometry
}

function copyAttributeComponents(
  source: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  target: Float32Array
): void {
  const itemSize = source.itemSize
  for (let item = 0; item < source.count; item += 1) {
    const offset = item * itemSize
    target[offset] = source.getX(item)
    if (itemSize > 1) {
      target[offset + 1] = source.getY(item)
    }
    if (itemSize > 2) {
      target[offset + 2] = source.getZ(item)
    }
    if (itemSize > 3) {
      target[offset + 3] = source.getW(item)
    }
    for (let component = 4; component < itemSize; component += 1) {
      target[offset + component] = source.getComponent(item, component)
    }
  }
}

function createOcclusionDepthWriterKey(bindingId: string): string {
  return `${bindingId}:occlusion-depth`
}

function createOcclusionDepthWriterSignature(
  ownerObject: THREE.Object3D,
  geometry: THREE.BufferGeometry
): string {
  ownerObject.updateMatrixWorld(true)
  return [
    ownerObject.visible ? '1' : '0',
    ownerObject.layers.mask,
    geometry.id,
    ownerObject.matrixWorld.elements.map((value) => value.toFixed(6)).join(','),
  ].join('|')
}

function createRequestedDebugSnapshot(
  role: SpatialRepresentationRole,
  request: SpatialWorldLoadRequest
) {
  const debug = request.representations[role]?.debug
  return {
    prototypeName: `${role}-prototype`,
    meshVisible: debug?.meshVisible ?? false,
    boundsVisible: debug?.boundsVisible ?? false,
    boundsDepth: debug?.boundsDepth ?? 10,
  }
}

function getInverseOwnerMatrix(ownerObject: THREE.Object3D): THREE.Matrix4 {
  return new THREE.Matrix4().copy(ownerObject.matrixWorld).invert()
}

function getOwnerScale(ownerObject: THREE.Object3D): number {
  const scale = new THREE.Vector3()
  ownerObject.getWorldScale(scale)
  return Math.max(Math.abs(scale.x), Math.abs(scale.y), Math.abs(scale.z), 1e-8)
}

function createLocalCapsule(
  ownerObject: THREE.Object3D,
  capsule: SpatialCapsule,
  scale = getOwnerScale(ownerObject)
): SpatialCapsule {
  const inverse = getInverseOwnerMatrix(ownerObject)
  return {
    start: capsule.start.clone().applyMatrix4(inverse),
    end: capsule.end.clone().applyMatrix4(inverse),
    radius: capsule.radius / scale,
  }
}

function cloneWorldCapsule(capsule: SpatialCapsule): SpatialCapsule {
  return {
    start: capsule.start.clone(),
    end: capsule.end.clone(),
    radius: capsule.radius,
  }
}

function translateWorldCapsule(capsule: SpatialCapsule, motion: THREE.Vector3): void {
  capsule.start.add(motion)
  capsule.end.add(motion)
}

function normalizeWorldHitNormal(hit: SpatialCollisionHit): THREE.Vector3 | null {
  const source = hit.separationNormal ?? hit.normal
  if (!source || source.lengthSq() <= 1e-10) {
    return null
  }

  return source.clone().normalize()
}

function cloneRepresentationSnapshot(
  record: SpatialRepresentationRecord
): SpatialRepresentationSnapshot {
  return {
    role: record.role,
    state: record.state,
    format: record.format,
    backend: record.backend,
    bounds: record.bounds?.clone() ?? null,
    error: record.error,
    userData: record.userData,
    metadata: record.metadata,
    debug: record.debug ? { ...record.debug } : null,
  }
}

function pickBestSurfaceHit(
  hits: SpatialSurfaceHit[],
  bindingStore: SpatialBindingStore
): SpatialSurfaceHit | null {
  hits.sort((left, right) => compareHits(left, right, bindingStore))
  return hits[0] ?? null
}

function pickBestCollisionHit(
  hits: SpatialCollisionHit[],
  bindingStore: SpatialBindingStore
): SpatialCollisionHit | null {
  hits.sort((left, right) => compareHits(left, right, bindingStore))
  return hits[0] ?? null
}

function pickBestCollisionGroundHit(
  hits: SpatialCollisionGroundHit[],
  bindingStore: SpatialBindingStore
): SpatialCollisionGroundHit | null {
  hits.sort((left, right) => compareHits(left, right, bindingStore))
  return hits[0] ?? null
}

function pickBestNavigationHit(
  hits: SpatialNavigationHit[],
  bindingStore: SpatialBindingStore
): SpatialNavigationHit | null {
  hits.sort((left, right) => compareHits(left, right, bindingStore))
  return hits[0] ?? null
}

function pickBestNavigationMotionResult(
  hits: SpatialNavigationMotionResult[],
  bindingStore: SpatialBindingStore
): SpatialNavigationMotionResult | null {
  hits.sort((left, right) => compareHits(left, right, bindingStore))
  return hits[0] ?? null
}

function compareHits(
  left: { bindingId: string; distance: number; travelDistance?: number },
  right: { bindingId: string; distance: number; travelDistance?: number },
  bindingStore: SpatialBindingStore
): number {
  const leftBinding = bindingStore.get(left.bindingId)
  const rightBinding = bindingStore.get(right.bindingId)
  const priorityDelta = (rightBinding?.priority ?? 0) - (leftBinding?.priority ?? 0)
  if (priorityDelta !== 0) {
    return priorityDelta
  }

  const leftTravel = left.travelDistance ?? left.distance
  const rightTravel = right.travelDistance ?? right.distance
  if (leftTravel !== rightTravel) {
    return leftTravel - rightTravel
  }

  return left.distance - right.distance
}

function createDebugInstanceKey(bindingId: string, role: SpatialRepresentationRole): string {
  return `${bindingId}:${role}`
}

function syncDebugInstanceTransform(
  instance: THREE.Object3D,
  ownerObject: THREE.Object3D,
  debugRoot: THREE.Object3D
): void {
  const relativeMatrix = new THREE.Matrix4()
    .copy(debugRoot.matrixWorld)
    .invert()
    .multiply(ownerObject.matrixWorld)
  relativeMatrix.decompose(instance.position, instance.quaternion, instance.scale)
  syncLayersRecursive(instance, ownerObject.layers.mask)
  instance.visible = ownerObject.visible
  instance.updateMatrix()
  instance.updateMatrixWorld(true)
}

function disposeDebugObject(object3D: THREE.Object3D | null | undefined): void {
  if (!object3D) {
    return
  }

  object3D.traverse((child) => {
    const disposable = child as unknown as { dispose?: () => void }
    if (
      disposable.dispose &&
      !(child instanceof THREE.Mesh) &&
      !(child instanceof THREE.LineSegments)
    ) {
      disposable.dispose()
    }

    if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
      if (child.userData.disposeGeometry === true) {
        child.geometry.dispose()
      }
      const material = child.material
      if (Array.isArray(material)) {
        for (const entry of material) {
          entry.dispose()
        }
      } else {
        material?.dispose()
      }
    }
  })

  object3D.removeFromParent()
}

function syncLayersRecursive(object3D: THREE.Object3D, mask: number): void {
  object3D.layers.mask = mask
  for (const child of object3D.children) {
    syncLayersRecursive(child, mask)
  }
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }

  return new Error(String(error))
}
